import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { appActions, agenciesActions } from 'src/store/actions';
import { GridWrapper, Button } from 'src/components';
import { Page } from 'src/containers';
import { Container, Col, Div, Icon, Modal, Row, Text } from '@pinpoint/design-kit';
import CONSTANTS from 'src/store/constants';

// eslint-disable-next-line react/prop-types
function Actions({ id, setAgencyId, showConfirmModal }) {
  const history = useHistory();
  return (
    <Div d="flex" m={{ x: '-0.5rem' }}>
      <Button
        buttonStyle="primary"
        onClick={() => {
          history.push(`/agencies/${id}`);
        }}
        data-tip="edit"
      >
        <Icon color="gray400" name="Edit" />
      </Button>
      <Button
        buttonStyle="primary"
        onClick={() => {
          setAgencyId(id);
          showConfirmModal(true);
        }}
        data-tip="delete"
      >
        <Icon color="gray400" name="Delete" />
      </Button>
    </Div>
  );
}

function Agencies({
  history,
  agencies,
  fetchAgenciesStatus,
  deleteAgencyStatus,
  deleteAgencyError,
  fetchAgencies,
  deleteAgency,
  enqueueNotification,
}) {
  const [isOpen, showConfirmModal] = useState(false);
  const [agencyId, setAgencyId] = useState(null);
  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Website',
        accessor: 'website',
      },
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Actions',
        // eslint-disable-next-line react/prop-types
        accessor: ({ id }) => Actions({ id, showConfirmModal, setAgencyId }),
      },
    ],
    []
  );

  const data = agencies;

  const getData = useCallback(() => {
    fetchAgencies();
  }, [fetchAgencies]);

  const hasData = agencies && agencies.length > 0;

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (deleteAgencyStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Editing the agency failed. Reason: ${deleteAgencyError}`);
    } else if (deleteAgencyStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Agency deactivated successfully', { autoClose: true });
      getData();
    }
  }, [deleteAgencyStatus, deleteAgencyError, enqueueNotification, getData]);

  return (
    <Container>
      <Modal
        align="center"
        rounded="md"
        isOpen={isOpen}
        onClose={() => {
          showConfirmModal(false);
        }}
      >
        <Icon
          name="Cross"
          pos="absolute"
          top="1rem"
          right="1rem"
          size="16px"
          onClick={() => {
            showConfirmModal(false);
          }}
          cursor="pointer"
        />
        <Div d="flex" flexDir="column" justify="space-between">
          <Div d="flex" align="center" m={{ b: '4rem' }}>
            <Icon name="AlertSolid" color="warning700" m={{ r: '1rem' }} />
            <Text textSize="subheader">Do you want to delete the agency?</Text>
          </Div>
          <Div d="flex" justify="flex-end">
            <Button
              buttonStyle="secondary"
              m={{ r: '1rem' }}
              onClick={() => {
                showConfirmModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              buttonStyle="primary"
              onClick={() => {
                deleteAgency(agencyId);
                showConfirmModal(false);
              }}
            >
              Yes, Delete
            </Button>
          </Div>
        </Div>
      </Modal>
      <Div p={{ y: '2rem' }}>
        <Row d="flex" justify="flex-end">
          <Col>
            <Button
              buttonStyle="primary"
              w="100%"
              onClick={() => {
                history.push('/agencies/add');
              }}
            >
              <Icon color="white" name="Plus" m={{ r: '.5em' }} />
              <Text color="white">Add</Text>
            </Button>
          </Col>
        </Row>
        <Row>
          <Col size="12">
            <GridWrapper
              hasData={hasData}
              getData={getData}
              columns={columns}
              data={data}
              apiStatus={fetchAgenciesStatus}
              hasGlobalFilter
              paginate
            />
          </Col>
        </Row>
      </Div>
    </Container>
  );
}

const mapStateToProps = ({ agencies }, ownProps) => ({ ...agencies, ...ownProps });

const mapDispatchToProps = (dispatch) => ({
  fetchAgencies: () => dispatch(agenciesActions.fetchAgencies()),
  deleteAgency: (agencyId) => dispatch(agenciesActions.deleteAgency(agencyId)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

Agencies.propTypes = {
  agencies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ).isRequired,
  fetchAgencies: PropTypes.func.isRequired,
  fetchAgenciesStatus: PropTypes.oneOf([
    CONSTANTS.API.STARTED,
    CONSTANTS.API.SUCCEEDED,
    CONSTANTS.API.FAILED,
    undefined,
  ]).isRequired,
  deleteAgencyStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
    .isRequired,
  deleteAgencyError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  deleteAgency: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(Agencies));
